import { useState, useCallback, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { validateTimeRecord } from '../../utils/timeValidation';
import { getEmptyTimeRecord } from '../../utils/formHelpers';
import { deleteTimeRecord, upsertTimeRecord } from '../../lib/timeRecords';
import type { TimeRecord } from '../../types/timeRecord';
import { handleDatabaseError } from '../../utils/errorHandling';
import type { PostgrestError } from '@supabase/supabase-js';

export const useTimeRecordForm = (initialDate: string) => {
  const [formState, setFormState] = useState<TimeRecord>(getEmptyTimeRecord(initialDate));
  const [originalState, setOriginalState] = useState<TimeRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [hasRecord, setHasRecord] = useState(false);

  const loadTimeRecord = async (date: string, userId: string) => {
    if (!userId) {
      console.error('User ID is required to load time record');
      return;
    }

    setLoading(true);
    try {
      const { data, error: supabaseError } = await supabase
        .from('time_records')
        .select()
        .eq('record_date', date)
        .eq('user_id', userId)
        .maybeSingle();

      if (supabaseError) throw supabaseError;

      if (data) {
        const newState = {
          recordDate: date,
          amIn: data.am_in || '',
          amOut: data.am_out || '',
          pmIn: data.pm_in || '',
          pmOut: data.pm_out || '',
          otIn: data.ot_in || '',
          otOut: data.ot_out || '',
          salesAmount: data.sales_amount || 0,
          caAmount: data.ca_amount || 0,
          comments: data.comments || ''
        };
        setFormState(newState);
        setOriginalState(newState);
        setHasRecord(true);
      } else {
        const emptyState = getEmptyTimeRecord(date);
        setFormState(emptyState);
        setOriginalState(null);
        setHasRecord(false);
      }
      setError(null);
    } catch (err: any) {
      console.error('Failed to load time record:', err);
      setError(err?.message || 'An unknown error occurred');
    } finally {
      setLoading(false);
      setIsDirty(false);
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        loadTimeRecord(initialDate, user.id);
      }
    };
    loadInitialData();
  }, [initialDate]);

  const handleTimeChange = useCallback((name: string, value: string) => {
    setFormState(prev => {
      const newState = { 
        ...prev, 
        [name]: name.includes('Amount') ? Number(value) || 0 : value 
      };
      setIsDirty(JSON.stringify(newState) !== JSON.stringify(originalState));
      return newState;
    });
    setError(null);
    setSuccess(false);
  }, [originalState]);

  const handleDateChange = useCallback(async (date: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setError('User authentication required');
      return;
    }
    await loadTimeRecord(date, user.id);
  }, []);

  const handleSubmit = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setError('User authentication required');
      return;
    }

    setLoading(true);
    try {
      const { error: submitError } = await upsertTimeRecord({
        userId: user.id,
        recordDate: formState.recordDate,
        amIn: formState.amIn,
        amOut: formState.amOut,
        pmIn: formState.pmIn,
        pmOut: formState.pmOut,
        otIn: formState.otIn,
        otOut: formState.otOut,
        salesAmount: formState.salesAmount || 0,
        caAmount: formState.caAmount || 0,
        comments: formState.comments || ''
      });

      if (submitError) throw submitError;

      setOriginalState({ ...formState });
      setIsDirty(false);
      setSuccess(true);
      setError(null);
    } catch (err: any) {
      console.error('Error saving record:', err);
      setError(err?.message || 'Failed to save record');
    } finally {
      setLoading(false);
    }
  }, [formState]);

  const handleReset = useCallback(() => {
    setFormState(originalState || getEmptyTimeRecord(formState.recordDate));
    setError(null);
    setSuccess(false);
    setIsDirty(false);
  }, [originalState, formState.recordDate]);

  const handleDelete = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setError('User authentication required');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this record?')) {
      return;
    }

    setLoading(true);
    try {
      const { error: deleteError } = await deleteTimeRecord(formState.recordDate, user.id);
      if (deleteError) throw deleteError;
      
      const emptyState = getEmptyTimeRecord(formState.recordDate);
      setFormState(emptyState);
      setOriginalState(null);
      setHasRecord(false);
      setIsDirty(false);
      setSuccess(true);
    } catch (err: any) {
      console.error('Error deleting record:', err);
      setError(err?.message || 'Failed to delete record');
    } finally {
      setLoading(false);
    }
  }, [formState.recordDate]);

  const validateTimePairs = (formState: any) => {
    // Morning pair
    if ((formState.amIn && !formState.amOut) || (!formState.amIn && formState.amOut)) {
      throw new Error('Morning time in/out must be filled in pairs');
    }
    
    // Afternoon pair
    if ((formState.pmIn && !formState.pmOut) || (!formState.pmIn && formState.pmOut)) {
      throw new Error('Afternoon time in/out must be filled in pairs');
    }
    
    // Overtime pair
    if ((formState.otIn && !formState.otOut) || (!formState.otIn && formState.otOut)) {
      throw new Error('Overtime time in/out must be filled in pairs');
    }
  };

  return {
    formState,
    setFormState,
    error,
    success,
    loading,
    isDirty,
    hasRecord,
    loadTimeRecord,
    handleTimeChange,
    handleDateChange,
    handleSubmit,
    handleReset,
    handleDelete
  };
};